import { Assistant } from "@inrev/assistant";
import { Dtos } from "@inrev/common";
import { useFetchSfaaClassExperienceSeries } from "@portal/api/sfaa-class-experience/admin";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { Spinner } from "@portal/components/primitives";
import { useMemo } from "react";
import { SfaaLossRatioChart } from "../../../../../market-analytics/SfaaLossRatioChart";

const { US_TOTAL_LOCATION } = Dtos.Admin.SfaaClassExperience;

type SeriesEntity = Dtos.Admin.SfaaClassExperience.Series.Entity;
type Metric = Assistant.Charts.SfaaChart["metric"];

const METRIC_CAPTION: Record<Metric, string> = {
	lossRatio: "Incurred loss over earned premium",
	trailingLossRatio: "Three-year incurred loss over earned premium",
};

/**
 * The entity with its legend average taken over the shown years only, the same plain mean of
 * yearly ratios the API takes over all of them.
 */
const withinYears = (
	entity: SeriesEntity,
	years: readonly number[],
	metric: Metric,
): SeriesEntity => {
	const shown = new Set(years);
	const values = entity.points
		.filter((point) => shown.has(point.year))
		.map((point) => point[metric])
		.filter((value) => value !== null);
	const average =
		values.length === 0 ? null : values.reduce((total, value) => total + value, 0) / values.length;
	return {
		...entity,
		stats: { ...entity.stats, [metric]: { ...entity.stats[metric], average } },
	};
};

export const AdminAssistantSfaaChart = ({ chart }: { chart: Assistant.Charts.SfaaChart }) => {
	// A state focus overlays the national series; a national focus suppresses the comparison.
	const series = useFetchSfaaClassExperienceSeries({
		line: chart.line,
		subline: chart.subline,
		codeSeries: chart.codeSeries,
		sfaaCode: chart.sfaaCode,
		location: chart.location,
		comparison: US_TOTAL_LOCATION,
	});

	const shown = useMemo(() => {
		if (series.data === undefined) return undefined;
		const { fromYear, toYear, metric } = chart;
		const years = series.data.years.filter(
			(year) =>
				(fromYear === undefined || year >= fromYear) && (toYear === undefined || year <= toYear),
		);
		const { selected, comparison } = series.data;
		return {
			years,
			selected: withinYears(selected, years, metric),
			comparison: comparison === null ? null : withinYears(comparison, years, metric),
		};
	}, [series.data, chart]);

	// A failed refetch keeps the chart already drawn, as the Market Analytics tab does.
	if (shown === undefined && series.error !== null) {
		return (
			<ReportedRetryableLoadError
				resource="SFAA series"
				error={series.error}
				onRetry={series.refetch}
				retrying={series.isFetching}
			/>
		);
	}
	if (shown === undefined) return <Spinner />;

	return (
		<SfaaLossRatioChart
			title={Assistant.Charts.sfaaChartTitle(chart)}
			caption={`${METRIC_CAPTION[chart.metric]}, ${chart.location}.`}
			years={shown.years}
			selected={shown.selected}
			comparison={shown.comparison}
			metric={chart.metric}
			testId="assistant-sfaa-chart"
		/>
	);
};
